import React, { createContext, useContext } from "react";
import { getLocalStorageItem, setLocalStorageItem } from "./useLocalStorage";
import useReducerWithSideEffects, {
  UpdateWithSideEffect,
} from "use-reducer-with-side-effects";
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from "./useSessionStorage";

const AppContext = createContext();

const reducer = (prevState, action) => {
  const { type } = action;
  if (type === SET_TOKEN_LOCAL) {
    const {
      payload: {
        access,
        refresh,
        id,
        userId,
        userName,
        userAuthority,
        completedCallback,
      },
    } = action;

    return UpdateWithSideEffect(
      {
        ...prevState,
        access,
        refresh,
        id,
        userId,
        userName,
        userAuthority,
        completedCallback,
      },
      (state, dispatch) => {
        setLocalStorageItem("access", access);
        setLocalStorageItem("refresh", refresh);
        setLocalStorageItem("id", id);
        setLocalStorageItem("userId", userId);
        setLocalStorageItem("userName", userName);
        setLocalStorageItem("userAuthority", userAuthority);

        window.ReactNativeWebView?.postMessage("sync-storage");
        completedCallback();
      }
    );
  } else if (type === SET_TOKEN_SESSION) {
    const {
      payload: {
        access,
        refresh,
        id,
        userId,
        userName,
        userAuthority,
        isAudioAllowed,
        completedCallback,
      },
    } = action;

    return UpdateWithSideEffect(
      {
        ...prevState,
        access,
        refresh,
        id,
        userId,
        userName,
        userAuthority,
        isAudioAllowed,
        completedCallback,
      },
      (state, dispatch) => {
        setSessionStorageItem("access", access);
        setSessionStorageItem("refresh", refresh);
        setSessionStorageItem("id", id);
        setSessionStorageItem("userId", userId);
        setSessionStorageItem("userName", userName);
        setSessionStorageItem("userAuthority", userAuthority);
        setSessionStorageItem("isAudioAllowed", isAudioAllowed);

        completedCallback();
      }
    );
  } else if (type === UPDATE_TOKEN_SESSION) {
    const { payload: isAudioAllowed } = action;
    return UpdateWithSideEffect(
      {
        ...prevState,
        isAudioAllowed,
      },
      (state, dispatch) => {
        setSessionStorageItem("isAudioAllowed", isAudioAllowed);
      }
    );
  } else if (type === DELETE_TOKEN) {
    return UpdateWithSideEffect(
      {
        ...prevState,
        access: "",
        refresh: "",
        id: "",
        userId: "",
        userName: "",
        userAuthority: "",
        isAudioAllowed: false,
      },
      (state, dispatch) => {
        setLocalStorageItem("access", "");
        setLocalStorageItem("refresh", "");
        setLocalStorageItem("id", "");
        setLocalStorageItem("userId", "");
        setLocalStorageItem("userName", "");
        setLocalStorageItem("userAuthority", "");

        setSessionStorageItem("access", "");
        setSessionStorageItem("refresh", "");
        setSessionStorageItem("id", "");
        setSessionStorageItem("userId", "");
        setSessionStorageItem("userName", "");
        setSessionStorageItem("userAuthority", "");
        setSessionStorageItem("isAudioAllowed", false);

        window.ReactNativeWebView?.postMessage("sync-storage");
      }
    );
  } else if (type === SET_CURRENT_URL) {
    const { payload: currentUrl } = action;
    return UpdateWithSideEffect(
      {
        ...prevState,
        currentUrl,
      },
      (state, dispatch) => {
        setLocalStorageItem("currentUrl", currentUrl);
      }
    );
  }

  return prevState;
};

export const AppProvider = ({ children }) => {
  let defaultaccess = getSessionStorageItem("access", "");
  let defaultrefresh = getSessionStorageItem("refresh", "");
  let defaultId = getSessionStorageItem("id", "");
  let defaultUserId = getSessionStorageItem("userId", "");
  let defaultUserName = getSessionStorageItem("userName", "");
  let defaultUserAuthority = getSessionStorageItem("userAuthority", "");
  let defaultIsAudioAllowed = getSessionStorageItem("isAudioAllowed", false); //isAudioAllowed는 세션에서만 사용

  if (!defaultaccess) {
    defaultaccess = getLocalStorageItem("access", "");
    defaultrefresh = getLocalStorageItem("refresh", "");
    defaultId = getLocalStorageItem("id", "");
    defaultUserId = getLocalStorageItem("userId", "");
    defaultUserName = getLocalStorageItem("userName", "");
    defaultUserAuthority = getLocalStorageItem("userAuthority", "");
  }
  const [store, dispatch] = useReducerWithSideEffects(reducer, {
    access: defaultaccess,
    refresh: defaultrefresh,
    isAuthenticated: defaultaccess.length > 0,
    id: defaultId,
    userId: defaultUserId,
    userName: defaultUserName,
    userAuthority: defaultUserAuthority,
    currentUrl: "",
    isAudioAllowed: defaultIsAudioAllowed,
  });

  return (
    <AppContext.Provider value={{ store, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);

//Actions
const SET_TOKEN_LOCAL = "APP/SET_TOKEN";
const SET_TOKEN_SESSION = "APP/SET_TOKEN_SESSION";
const UPDATE_TOKEN_SESSION = "APP/UPDATE_TOKEN_SESSION";
const DELETE_TOKEN = "APP/DELETE_TOKEN";
const SET_CURRENT_URL = "APP/SET_CURRENT_URL";

//Action Creators
export const setTokenInLocal = (
  access,
  refresh,
  id,
  userId,
  userName,
  userAuthority,
  completedCallback
) => ({
  type: SET_TOKEN_LOCAL,
  payload: {
    access,
    refresh,
    id,
    userId,
    userName,
    userAuthority,
    completedCallback,
  },
});
export const setTokenInSession = (
  access,
  refresh,
  id,
  userId,
  userName,
  userAuthority,
  isAudioAllowed,
  completedCallback
) => ({
  type: SET_TOKEN_SESSION,
  payload: {
    access,
    refresh,
    id,
    userId,
    userName,
    userAuthority,
    isAudioAllowed,
    completedCallback,
  },
});
export const updateTokenInSession = (isAudioAllowed) => ({
  type: UPDATE_TOKEN_SESSION,
  payload: isAudioAllowed,
});
export const deleteToken = () => ({ type: DELETE_TOKEN });
export const setCurrentURL = (currentUrl) => ({
  type: SET_CURRENT_URL,
  payload: currentUrl,
});
